import React, { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { BookOpen, Edit, Trash2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import QuestionManagementMenu from '@/components/admin/QuestionManagementMenu';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { DataTable } from '@/components/common/DataTable';
import { DataPagination } from '@/components/common/DataPagination';
import { ListToolbar } from '@/components/common/ListToolbar';
import { usePaginatedQuery } from '@/hooks/usePaginatedQuery';
import { queryKeys } from '@/lib/queryKeys';
import { useUrlState } from '@/lib/urlState';
import { exportRowsAsCsv } from '@/lib/csv';

interface Subject {
  id: string;
  name: string;
  description: string;
  grade: string;
}

interface Category {
  id: string;
  name: string;
  description: string;
  subject_id: string;
}

const AdminSubjects = () => {
  const url = useUrlState();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const tab = url.get('tab') ?? 'subjects';
  const search = url.get('q') ?? '';
  const subjectFilter = url.get('subject') ?? 'all';

  const [selectedSubject, setSelectedSubject] = useState<Subject | null>(null);
  const [newSubjectName, setNewSubjectName] = useState('');
  const [newSubjectDescription, setNewSubjectDescription] = useState('');
  const [newSubjectGrade, setNewSubjectGrade] = useState('');
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryDescription, setNewCategoryDescription] = useState('');
  const [editSubjectId, setEditSubjectId] = useState<string | null>(null);
  const [editCategory, setEditCategory] = useState<Category | null>(null);

  // Lightweight lookup of all subjects (for the category-tab dropdown + name resolution).
  const { data: allSubjects = [] } = useQuery({
    queryKey: ['admin-subjects-lookup'],
    queryFn: async () => {
      const { data, error } = await supabase.from('subjects').select('id, name, grade').order('name');
      if (error) throw error;
      return (data ?? []) as Pick<Subject, 'id' | 'name' | 'grade'>[];
    },
  });
  const subjectMap = useMemo(() => {
    const m = new Map<string, { name: string; grade: string }>();
    allSubjects.forEach((s) => m.set(s.id, { name: s.name, grade: s.grade }));
    return m;
  }, [allSubjects]);

  const subjectsPaginated = usePaginatedQuery<Subject>({
    queryKey: queryKeys.subjects.list({ page: 0, pageSize: 25, filters: { tab, search } }),
    table: 'subjects',
    orderBy: { column: 'name', ascending: true },
    syncToUrl: tab === 'subjects',
    enabled: tab === 'subjects',
    filters: (q) => (search ? q.or(`name.ilike.%${search}%,grade.ilike.%${search}%`) : q),
  });

  const categoriesPaginated = usePaginatedQuery<Category>({
    queryKey: queryKeys.subjects.list({
      page: 0,
      pageSize: 25,
      filters: { tab, search, subjectFilter },
    }),
    table: 'subjects_categories',
    orderBy: { column: 'name', ascending: true },
    syncToUrl: tab === 'categories',
    enabled: tab === 'categories',
    filters: (q) => {
      let next: any = q;
      if (subjectFilter !== 'all') next = next.eq('subject_id', subjectFilter);
      if (search) next = next.or(`name.ilike.%${search}%`);
      return next;
    },
  });

  // Reset page on filter changes.
  useEffect(() => {
    if (tab === 'subjects') subjectsPaginated.setPage(0);
    else categoriesPaginated.setPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, search, subjectFilter]);

  const refreshSubjects = () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.subjects.all });
    queryClient.invalidateQueries({ queryKey: ['admin-subjects-lookup'] });
  };

  const handleCreateSubject = async () => {
    if (!newSubjectName || !newSubjectDescription || !newSubjectGrade) {
      toast({ title: 'Error', description: 'Please fill in all subject fields.', variant: 'destructive' });
      return;
    }
    const { error } = await supabase
      .from('subjects')
      .insert([{ name: newSubjectName, description: newSubjectDescription, grade: newSubjectGrade }]);
    if (error) return toast({ title: 'Error creating subject', description: error.message, variant: 'destructive' });
    toast({ title: 'Subject created', description: `Subject "${newSubjectName}" has been created.` });
    refreshSubjects();
    setNewSubjectName(''); setNewSubjectDescription(''); setNewSubjectGrade('');
  };

  const handleUpdateSubject = async () => {
    if (!editSubjectId) return;
    if (!newSubjectName || !newSubjectDescription || !newSubjectGrade) {
      toast({ title: 'Error', description: 'Please fill in all subject fields.', variant: 'destructive' });
      return;
    }
    const { error } = await supabase
      .from('subjects')
      .update({ name: newSubjectName, description: newSubjectDescription, grade: newSubjectGrade })
      .eq('id', editSubjectId);
    if (error) return toast({ title: 'Error updating subject', description: error.message, variant: 'destructive' });
    toast({ title: 'Subject updated', description: `Subject "${newSubjectName}" has been updated.` });
    refreshSubjects();
    setEditSubjectId(null); setNewSubjectName(''); setNewSubjectDescription(''); setNewSubjectGrade('');
  };

  const handleDeleteSubject = async (id: string) => {
    const { error } = await supabase.from('subjects').delete().eq('id', id);
    if (error) return toast({ title: 'Error deleting subject', description: error.message, variant: 'destructive' });
    toast({ title: 'Subject deleted', description: 'Subject has been deleted.' });
    refreshSubjects();
  };

  const handleCreateCategory = async () => {
    if (!selectedSubject) {
      toast({ title: 'Error', description: 'Please select a subject first.', variant: 'destructive' });
      return;
    }
    if (!newCategoryName || !newCategoryDescription) {
      toast({ title: 'Error', description: 'Please fill in all category fields.', variant: 'destructive' });
      return;
    }
    const { error } = await supabase
      .from('subjects_categories')
      .insert([{ name: newCategoryName, description: newCategoryDescription, subject_id: selectedSubject.id }]);
    if (error) return toast({ title: 'Error creating category', description: error.message, variant: 'destructive' });
    toast({ title: 'Category created', description: `Category "${newCategoryName}" has been created.` });
    queryClient.invalidateQueries({ queryKey: queryKeys.subjects.all });
    setNewCategoryName(''); setNewCategoryDescription('');
  };

  const handleUpdateCategory = async () => {
    if (!editCategory) return;
    if (!newCategoryName || !newCategoryDescription) {
      toast({ title: 'Error', description: 'Please fill in all category fields.', variant: 'destructive' });
      return;
    }
    const { error } = await supabase
      .from('subjects_categories')
      .update({ name: newCategoryName, description: newCategoryDescription })
      .eq('id', editCategory.id);
    if (error) return toast({ title: 'Error updating category', description: error.message, variant: 'destructive' });
    toast({ title: 'Category updated', description: `Category "${newCategoryName}" has been updated.` });
    queryClient.invalidateQueries({ queryKey: queryKeys.subjects.all });
    setEditCategory(null); setNewCategoryName(''); setNewCategoryDescription('');
  };

  const handleDeleteCategory = async (id: string) => {
    const { error } = await supabase.from('subjects_categories').delete().eq('id', id);
    if (error) return toast({ title: 'Error deleting category', description: error.message, variant: 'destructive' });
    toast({ title: 'Category deleted', description: 'Category has been deleted.' });
    queryClient.invalidateQueries({ queryKey: queryKeys.subjects.all });
  };

  const subjectColumns: ColumnDef<Subject>[] = useMemo(() => [
    { header: 'Name', accessorKey: 'name' },
    { header: 'Grade', accessorKey: 'grade', size: 100 },
    { header: 'Description', accessorKey: 'description' },
    {
      id: 'actions',
      size: 120,
      header: () => <div className="text-right">Actions</div>,
      cell: ({ row }) => (
        <div className="flex justify-end gap-2">
          <Button variant="secondary" size="icon" onClick={() => {
            setEditSubjectId(row.original.id);
            setNewSubjectName(row.original.name);
            setNewSubjectDescription(row.original.description);
            setNewSubjectGrade(row.original.grade);
          }}>
            <Edit className="h-4 w-4" />
          </Button>
          <Button variant="destructive" size="icon" onClick={() => handleDeleteSubject(row.original.id)}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], []);

  const categoryColumns: ColumnDef<Category>[] = useMemo(() => [
    { header: 'Name', accessorKey: 'name' },
    { header: 'Description', accessorKey: 'description' },
    {
      header: 'Subject',
      accessorKey: 'subject_id',
      cell: ({ row }) => {
        const s = subjectMap.get(row.original.subject_id);
        return s ? `${s.name} (${s.grade})` : 'Unknown';
      },
    },
    {
      id: 'actions',
      size: 120,
      header: () => <div className="text-right">Actions</div>,
      cell: ({ row }) => (
        <div className="flex justify-end gap-2">
          <Button variant="secondary" size="icon" onClick={() => {
            setEditCategory(row.original);
            setNewCategoryName(row.original.name);
            setNewCategoryDescription(row.original.description);
          }}>
            <Edit className="h-4 w-4" />
          </Button>
          <Button variant="destructive" size="icon" onClick={() => handleDeleteCategory(row.original.id)}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [subjectMap]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 flex flex-col">
      <Header />
      <div className="flex-1 container mx-auto px-4 py-8">
          <h1 className="text-3xl font-bold mb-6 flex items-center gap-2">
            <BookOpen className="h-8 w-8" />
            Subject & Category Management
          </h1>
          <QuestionManagementMenu />

          <Tabs value={tab} onValueChange={(v) => url.set({ tab: v, q: null, subject: null, page: null })} className="w-full">
            <TabsList>
              <TabsTrigger value="subjects">Subjects</TabsTrigger>
              <TabsTrigger value="categories">Categories</TabsTrigger>
            </TabsList>

            <TabsContent value="subjects">
              <Card>
                <CardHeader><CardTitle>Manage Subjects</CardTitle></CardHeader>
                <CardContent>
                  <div className="grid gap-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <Label htmlFor="subject-name">Subject Name</Label>
                        <Input id="subject-name" value={newSubjectName} onChange={(e) => setNewSubjectName(e.target.value)} />
                      </div>
                      <div>
                        <Label htmlFor="subject-grade">Grade</Label>
                        <Input id="subject-grade" value={newSubjectGrade} onChange={(e) => setNewSubjectGrade(e.target.value)} placeholder="e.g., Grade 10" />
                      </div>
                      <div>
                        <Label htmlFor="subject-description">Subject Description</Label>
                        <Textarea id="subject-description" value={newSubjectDescription} onChange={(e) => setNewSubjectDescription(e.target.value)} />
                      </div>
                    </div>
                    {editSubjectId
                      ? <Button onClick={handleUpdateSubject}>Update Subject</Button>
                      : <Button onClick={handleCreateSubject}>Create Subject</Button>}
                  </div>

                  <div className="mt-6 space-y-3">
                    <ListToolbar
                      search={search}
                      onSearchChange={(v) => url.set({ q: v || null })}
                      searchPlaceholder="Search subjects…"
                      onExportCsv={() => exportRowsAsCsv(`subjects-page-${subjectsPaginated.page + 1}.csv`, subjectsPaginated.rows as unknown as Record<string, unknown>[])}
                    />
                    <DataTable
                      columns={subjectColumns}
                      data={subjectsPaginated.rows}
                      isLoading={subjectsPaginated.isLoading}
                      isError={subjectsPaginated.isError}
                      onRetry={subjectsPaginated.refetch}
                      emptyTitle="No subjects"
                      getRowId={(row) => row.id}
                    />
                    <DataPagination
                      page={subjectsPaginated.page}
                      pageSize={subjectsPaginated.pageSize}
                      total={subjectsPaginated.total}
                      pageCount={subjectsPaginated.pageCount}
                      onPageChange={subjectsPaginated.setPage}
                      onPageSizeChange={subjectsPaginated.setPageSize}
                      isFetching={subjectsPaginated.isFetching}
                    />
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="categories">
              <Card>
                <CardHeader><CardTitle>Manage Categories</CardTitle></CardHeader>
                <CardContent>
                  <div className="grid gap-4">
                    <Select onValueChange={(value) => {
                      const selected = allSubjects.find((s) => s.id === value);
                      setSelectedSubject(selected ? { ...selected, description: '' } as Subject : null);
                    }}>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select a subject" />
                      </SelectTrigger>
                      <SelectContent>
                        {allSubjects.map((subject) => (
                          <SelectItem key={subject.id} value={subject.id}>
                            {subject.name} ({subject.grade})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="category-name">Category Name</Label>
                        <Input id="category-name" value={newCategoryName} onChange={(e) => setNewCategoryName(e.target.value)} />
                      </div>
                      <div>
                        <Label htmlFor="category-description">Category Description</Label>
                        <Textarea id="category-description" value={newCategoryDescription} onChange={(e) => setNewCategoryDescription(e.target.value)} />
                      </div>
                    </div>
                    {editCategory
                      ? <Button onClick={handleUpdateCategory}>Update Category</Button>
                      : <Button onClick={handleCreateCategory}>Create Category</Button>}
                  </div>

                  <div className="mt-6 space-y-3">
                    <ListToolbar
                      search={search}
                      onSearchChange={(v) => url.set({ q: v || null })}
                      searchPlaceholder="Search categories…"
                      filters={
                        <Select value={subjectFilter} onValueChange={(v) => url.set({ subject: v === 'all' ? null : v })}>
                          <SelectTrigger className="w-[220px]"><SelectValue placeholder="Filter by subject" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All Subjects</SelectItem>
                            {allSubjects.map((s) => (
                              <SelectItem key={s.id} value={s.id}>{s.name} ({s.grade})</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      }
                      onExportCsv={() => exportRowsAsCsv(`categories-page-${categoriesPaginated.page + 1}.csv`, categoriesPaginated.rows as unknown as Record<string, unknown>[])}
                    />
                    <DataTable
                      columns={categoryColumns}
                      data={categoriesPaginated.rows}
                      isLoading={categoriesPaginated.isLoading}
                      isError={categoriesPaginated.isError}
                      onRetry={categoriesPaginated.refetch}
                      emptyTitle="No categories"
                      getRowId={(row) => row.id}
                    />
                    <DataPagination
                      page={categoriesPaginated.page}
                      pageSize={categoriesPaginated.pageSize}
                      total={categoriesPaginated.total}
                      pageCount={categoriesPaginated.pageCount}
                      onPageChange={categoriesPaginated.setPage}
                      onPageSizeChange={categoriesPaginated.setPageSize}
                      isFetching={categoriesPaginated.isFetching}
                    />
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
      </div>
      <Footer />
    </div>
  );
};

export default AdminSubjects;
